<template>
  <div class="flex flex-col rounded-xl bg-white p-6 shadow-md">
    <div
      class="order-1 mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
    >
      <div>
        <h1 class="text-xl font-bold text-purple-700">ثبت کد پیگیری</h1>
        <p class="mt-1 text-xs text-gray-500">
          کد رهگیری سفارش‌های در حال بسته‌بندی را ثبت کنید تا سفارش ارسال‌شده
          شود.
        </p>
      </div>

      <div
        class="min-w-[260px] rounded-xl border border-purple-100 bg-purple-50 p-3"
      >
        <label class="mb-2 block text-xs font-bold text-gray-700"
          >مسئول ثبت کد پیگیری <span class="text-red-500">*</span></label
        >
        <user-select
          v-model="responsibleId"
          user-type="paad"
          placeholder="مسئول را انتخاب کنید"
          :clearable="true"
          select-style="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-purple-500 focus:outline-none"
        ></user-select>
      </div>

      <label
        class="flex min-w-[260px] cursor-pointer items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3"
      >
        <input
          v-model="skipSms"
          type="checkbox"
          class="mt-1 h-4 w-4 accent-purple-600"
        />
        <span>
          <span class="block text-xs font-bold text-amber-800"
            >بدون ارسال پیامک</span
          >
          <span class="mt-1 block text-[10px] leading-5 text-amber-700">
            مسئول، کد رهگیری و تغییر وضعیت ثبت می‌شوند؛ فقط SMS ارسال نمی‌شود.
          </span>
        </span>
      </label>
    </div>

    <form
      class="order-3 mb-5 rounded-xl border border-purple-100 bg-purple-50 p-4"
      @submit.prevent="applyFilters"
    >
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <input
          v-model.trim="filters.orderId"
          type="number"
          min="1"
          placeholder="شماره سفارش"
          class="rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
        />
        <input
          v-model.trim="filters.phone"
          type="text"
          inputmode="tel"
          placeholder="شماره تلفن"
          class="rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
        />
        <input
          v-model.trim="filters.customerName"
          type="text"
          placeholder="نام کاربر سفارش"
          class="rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
        />
        <div class="flex gap-2">
          <button
            type="submit"
            class="flex-1 rounded-lg bg-purple-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-purple-700"
          >
            جستجو
          </button>
          <button
            type="button"
            class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-600 transition hover:bg-gray-50"
            @click="resetFilters"
          >
            پاک کردن
          </button>
        </div>
      </div>
    </form>

    <div v-if="loading" class="order-4 py-12 text-center text-sm text-gray-500">
      در حال دریافت سفارش‌ها...
    </div>

    <div v-else class="order-4 overflow-x-auto">
      <table class="min-w-full table-auto border border-gray-200 text-right">
        <thead class="bg-gray-50 text-gray-700">
          <tr>
            <th class="border p-3 text-center text-[10px]">ردیف</th>
            <th class="border p-3 text-center text-[10px]">شماره سفارش</th>
            <th class="border p-3 text-center text-[10px]">تاریخ</th>
            <th class="border p-3 text-center text-[10px]">کد اکسونی</th>
            <th class="border p-3 text-center text-[10px]">نام کاربر</th>
            <th class="border p-3 text-center text-[10px]">شماره تلفن</th>
            <th class="border p-3 text-center text-[10px]">وضعیت</th>
            <th class="min-w-[260px] border p-3 text-center text-[10px]">
              کد رهگیری پست
            </th>
            <th class="border p-3 text-center text-[10px]">جزئیات</th>
          </tr>
        </thead>
        <tbody class="text-sm text-gray-600">
          <tr
            v-for="(order, index) in orders"
            :key="order.id"
            class="transition hover:bg-gray-50"
          >
            <td class="border p-2 text-center">{{ pageOffset + index + 1 }}</td>
            <td class="border p-2 text-center font-bold text-gray-800">
              {{ order.id }}
            </td>
            <td class="border p-2 text-center text-[10px]">
              {{ formatDate(order.date_time) }}
            </td>
            <td class="border p-2 text-center text-[11px]">
              {{ order.exuni_tracking_code }}
            </td>
            <td class="border p-2 text-center text-[11px]">
              {{ customerName(order) }}
            </td>
            <td class="border p-2 text-center text-[11px]" dir="ltr">
              {{ customerPhone(order) }}
            </td>
            <td class="border p-2 text-center">
              <span
                class="rounded-full bg-indigo-100 px-2 py-1 text-[9px] font-bold text-indigo-700"
              >
                در حال بسته‌بندی
              </span>
            </td>
            <td class="border p-2" @keyup.enter="submitTrackingCode(order)">
              <div class="flex min-w-[250px] items-center gap-2">
                <input
                  :value="trackingCodes[order.id] || ''"
                  type="text"
                  maxlength="24"
                  inputmode="numeric"
                  :disabled="submittingOrderId === order.id"
                  :aria-label="`کد رهگیری سفارش ${order.id}`"
                  placeholder="کد رهگیری ۲۴ رقمی"
                  class="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none disabled:bg-gray-100"
                  @input="setTrackingCode(order.id, $event.target.value)"
                />
                <button
                  type="button"
                  :disabled="submittingOrderId !== null"
                  class="whitespace-nowrap rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-xs font-bold text-white transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                  @click="submitTrackingCode(order)"
                >
                  {{
                    submittingOrderId === order.id
                      ? "در حال ثبت..."
                      : "ثبت و ارسال"
                  }}
                </button>
              </div>
            </td>
            <td class="border p-2 text-center">
              <button
                type="button"
                class="text-xs font-bold text-blue-600 hover:underline"
                @click="$router.push(`/panel/adminOrderDetail/${order.id}`)"
              >
                مشاهده
              </button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="9"
              class="border p-10 text-center text-sm text-gray-500"
            >
              سفارش در حال بسته‌بندی با این فیلترها پیدا نشد.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      class="order-5 mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row"
    >
      <span class="text-xs text-gray-500">{{ totalCount }} سفارش</span>
      <div class="flex items-center gap-2">
        <button
          type="button"
          :disabled="currentPage <= 1 || loading"
          class="rounded-lg border border-gray-300 px-3 py-2 text-xs disabled:opacity-40"
          @click="goToPage(currentPage - 1)"
        >
          قبلی
        </button>
        <span
          class="rounded-lg bg-purple-50 px-3 py-2 text-xs font-bold text-purple-700"
        >
          صفحه {{ currentPage }} از {{ totalPages }}
        </span>
        <button
          type="button"
          :disabled="currentPage >= totalPages || loading"
          class="rounded-lg border border-gray-300 px-3 py-2 text-xs disabled:opacity-40"
          @click="goToPage(currentPage + 1)"
        >
          بعدی
        </button>
        <select
          v-model.number="pageSize"
          class="rounded-lg border border-gray-300 bg-white px-2 py-2 text-xs"
          @change="goToPage(1)"
        >
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <section
      class="order-2 mb-5 rounded-xl border border-blue-100 bg-blue-50 p-5"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <h2 class="text-base font-bold text-blue-800">
            ثبت گروهی از فایل اکسل
          </h2>
          <p class="mt-1 text-xs leading-6 text-blue-700">
            سیستم از ستون «نام گ» سفارش را با شماره سفارش، موبایل یا نام پیدا
            می‌کند و کد ۲۴ رقمی ستون «بارکد» و مبلغ ریالی ستون «هزینه کل» را می‌خواند.
          </p>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label
            class="cursor-pointer rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-bold text-blue-700 hover:bg-blue-50"
          >
            انتخاب فایل
            <input
              ref="importFile"
              type="file"
              accept=".xls,.xlsx,.html,.htm"
              :disabled="importSubmitting"
              class="hidden"
              @change="handleImportFile"
            />
          </label>
          <span class="max-w-[240px] truncate text-xs text-gray-600">{{
            importFile ? importFile.name : "فایلی انتخاب نشده"
          }}</span>
          <button
            type="button"
            :disabled="!importFile || importLoading || importSubmitting"
            class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            @click="previewImportFile"
          >
            {{ importLoading ? "در حال بررسی..." : "نمایش پیش‌نمایش" }}
          </button>
          <button
            v-if="importFile || importPreview.length"
            type="button"
            :disabled="importSubmitting"
            class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            @click="clearImport"
          >
            پاک کردن
          </button>
        </div>
      </div>

      <div v-if="importPreview.length" class="mt-5">
        <div class="mb-3 rounded-lg bg-white p-3 text-sm text-blue-800">
          جمع کل هزینه فایل: <b>{{ Number(importSummary.shipping_cost_total_rials || 0).toLocaleString('fa-IR') }} ریال</b>
          <span class="mr-4">جمع انتخاب‌شده: <b>{{ selectedImportCostTotal.toLocaleString('fa-IR') }} ریال</b></span>
          <p v-if="importSummary.invalid_cost_count" class="mt-2 text-xs text-red-600">{{ importSummary.invalid_cost_count }} ردیف هزینه نامعتبر دارد و در جمع محاسبه نشده است.</p>
        </div>
        <div class="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span class="rounded-full bg-white px-3 py-1 font-bold text-gray-700"
            >کل: {{ importSummary.total }}</span
          >
          <span
            class="rounded-full bg-green-100 px-3 py-1 font-bold text-green-700"
            >قابل ثبت: {{ importSummary.valid }}</span
          >
          <span class="rounded-full bg-red-100 px-3 py-1 font-bold text-red-700"
            >نیازمند بررسی: {{ importSummary.invalid }}</span
          >
          <span
            class="rounded-full bg-purple-100 px-3 py-1 font-bold text-purple-700"
            >انتخاب‌شده: {{ selectedImportOrderIds.length }}</span
          >
        </div>

        <div
          class="max-h-[520px] overflow-auto rounded-lg border border-blue-100 bg-white"
        >
          <table class="min-w-full table-auto text-right">
            <thead class="sticky top-0 z-10 bg-gray-50 text-gray-700">
              <tr>
                <th class="border p-2 text-center text-[10px]">انتخاب</th>
                <th class="border p-2 text-center text-[10px]">ردیف فایل</th>
                <th class="border p-2 text-center text-[10px]">نام گ</th>
                <th class="border p-2 text-center text-[10px]">بارکد</th>
                <th class="border p-2 text-center text-[10px]">هزینه کل (ریال)</th>
                <th class="border p-2 text-center text-[10px]">
                  سفارش پیدا‌شده
                </th>
                <th class="border p-2 text-center text-[10px]">نام مشتری</th>
                <th class="border p-2 text-center text-[10px]">موبایل</th>
                <th class="border p-2 text-center text-[10px]">روش تطبیق</th>
                <th class="border p-2 text-center text-[10px]">نتیجه</th>
              </tr>
            </thead>
            <tbody class="text-xs text-gray-600">
              <tr
                v-for="(row, index) in importPreview"
                :key="`${row.row_number}-${index}`"
                :class="importRowClass(row.status)"
              >
                <td class="border p-2 text-center">
                  <input
                    v-if="row.status === 'matched'"
                    v-model="selectedImportOrderIds"
                    type="checkbox"
                    :value="row.order_id"
                    :disabled="importSubmitting"
                    :aria-label="`انتخاب سفارش ${row.order_id}`"
                  />
                  <span v-else>—</span>
                </td>
                <td class="border p-2 text-center">{{ row.row_number }}</td>
                <td class="max-w-[220px] border p-2">
                  {{ row.source_value || "—" }}
                </td>
                <td class="border p-2 text-center font-mono" dir="ltr">
                  {{ row.tracking_code || "—" }}
                </td>
                <td class="border p-2 text-center">{{ row.shipping_cost_rials == null || row.shipping_cost_rials === "" ? "—" : row.shipping_cost_rials }}</td>
                <td class="border p-2 text-center font-bold">
                  <select
                    v-if="row.candidates && row.candidates.length > 1"
                    :value="row.selected_order_id || ''"
                    :disabled="importLoading || importSubmitting"
                    class="min-w-[230px] rounded-lg border border-amber-300 bg-white px-2 py-2 text-xs font-normal focus:border-purple-500 focus:outline-none"
                    @change="selectImportCandidate(row, $event.target.value)"
                  >
                    <option disabled value="">سفارش درست را انتخاب کنید</option>
                    <option
                      v-for="candidate in row.candidates"
                      :key="candidate.id"
                      :value="candidate.id"
                    >
                      {{ importCandidateLabel(candidate) }}
                    </option>
                  </select>
                  <span v-else>{{ row.order_id || candidateOrders(row) }}</span>
                </td>
                <td class="border p-2 text-center">
                  {{ row.customer_name || "—" }}
                </td>
                <td class="border p-2 text-center" dir="ltr">
                  {{ row.customer_phone || "—" }}
                </td>
                <td class="border p-2 text-center">
                  {{ row.match_method || "—" }}
                </td>
                <td class="border p-2 text-center">
                  <span
                    class="rounded-full px-2 py-1 text-[9px] font-bold"
                    :class="importStatusClass(row.status)"
                  >
                    {{ row.message }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="mt-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center"
        >
          <p class="text-xs text-gray-600">
            فقط ردیف‌های سبز و انتخاب‌شده ثبت می‌شوند. ردیف‌های قرمز هیچ تغییری
            در سفارش ایجاد نمی‌کنند. با دانلود فایل نتیجه، دلیل هر ردیف
            پیدا‌نشده جلوی همان ردیف نوشته می‌شود.
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="rounded-lg border border-blue-300 bg-white px-5 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-50"
              @click="downloadImportResult"
            >
              دانلود فایل همراه دلیل خطاها
            </button>
            <button
              type="button"
              :disabled="importSubmitting || !selectedImportOrderIds.length"
              class="rounded-lg bg-gradient-to-r from-green-600 to-emerald-500 px-5 py-2 text-sm font-bold text-white transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
              @click="confirmImport"
            >
              {{
                importSubmitting
                  ? "در حال ثبت..."
                  : `تأیید و ارسال ${selectedImportOrderIds.length} سفارش`
              }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <transition name="slide-fade">
      <div
        v-if="importProgress.show"
        class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-60 p-4"
        style="z-index: 9999"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tracking-import-progress-title"
      >
        <div
          class="w-full max-w-xl rounded-2xl bg-white p-6 text-right shadow-2xl"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3
                id="tracking-import-progress-title"
                class="text-lg font-bold text-purple-800"
              >
                وضعیت ثبت کدهای رهگیری
              </h3>
              <p class="mt-1 text-xs leading-6 text-gray-500">
                سفارش‌ها در چند بسته و به‌صورت هم‌زمان ثبت می‌شوند. تا پایان
                عملیات این صفحه را نبندید.
              </p>
            </div>
            <span
              class="rounded-full bg-purple-100 px-3 py-1 text-sm font-bold text-purple-700"
            >
              {{ importProgressPercent }}٪
            </span>
          </div>

          <div class="mt-5 h-4 overflow-hidden rounded-full bg-gray-200">
            <div
              class="h-full rounded-full bg-gradient-to-r from-purple-600 via-blue-500 to-emerald-500 transition-all duration-500"
              :style="{ width: `${importProgressPercent}%` }"
            ></div>
          </div>

          <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div class="rounded-xl bg-gray-50 p-3 text-center">
              <span class="block text-[10px] text-gray-500">کل سفارش‌ها</span>
              <strong class="mt-1 block text-base text-gray-800">{{
                importProgress.total
              }}</strong>
            </div>
            <div class="rounded-xl bg-blue-50 p-3 text-center">
              <span class="block text-[10px] text-blue-600">بررسی‌شده</span>
              <strong class="mt-1 block text-base text-blue-800">{{
                importProgress.completed
              }}</strong>
            </div>
            <div class="rounded-xl bg-green-50 p-3 text-center">
              <span class="block text-[10px] text-green-600">ثبت موفق</span>
              <strong class="mt-1 block text-base text-green-800">{{
                importProgress.succeeded
              }}</strong>
            </div>
            <div class="rounded-xl bg-red-50 p-3 text-center">
              <span class="block text-[10px] text-red-600">ثبت ناموفق</span>
              <strong class="mt-1 block text-base text-red-800">{{
                importProgress.failed
              }}</strong>
            </div>
          </div>

          <div
            v-if="!importProgress.done"
            class="mt-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700"
          >
            <span class="h-3 w-3 animate-pulse rounded-full bg-blue-500"></span>
            <span>
              در حال ثبت؛ {{ importProgress.activeBatches }} بسته هم‌زمان در حال
              پردازش است.
            </span>
          </div>

          <div
            v-else
            class="mt-4 rounded-xl border p-3 text-xs leading-6"
            :class="
              importProgress.failed
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-green-200 bg-green-50 text-green-700'
            "
          >
            {{ importProgressResultMessage }}
          </div>

          <div
            v-if="importProgress.smsFailedOrderIds.length"
            class="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-6 text-amber-800"
          >
            ثبت سفارش‌ها انجام شد، اما پیامک سفارش‌های
            {{ importProgress.smsFailedOrderIds.join("، ") }} ارسال نشد.
          </div>

          <div
            v-if="importProgress.failedBatches.length"
            class="mt-3 max-h-36 overflow-auto rounded-xl border border-red-100 bg-red-50 p-3"
          >
            <p class="mb-2 text-xs font-bold text-red-700">جزئیات خطاها:</p>
            <p
              v-for="(failure, index) in importProgress.failedBatches"
              :key="`${failure.orderIds.join('-')}-${index}`"
              class="mb-1 text-[10px] leading-5 text-red-700 last:mb-0"
            >
              سفارش‌های {{ failure.orderIds.join("، ") }}: {{ failure.message }}
            </p>
          </div>

          <div class="mt-5 flex justify-end">
            <button
              v-if="importProgress.done"
              type="button"
              class="rounded-lg bg-purple-700 px-5 py-2 text-sm font-bold text-white transition hover:bg-purple-800"
              @click="closeImportProgress"
            >
              بستن
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="slide-fade">
      <div
        v-if="notification.show"
        class="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border p-4 text-sm shadow-lg"
        :class="
          notification.type === 'success'
            ? 'border-green-200 bg-green-50 text-green-700'
            : 'border-red-200 bg-red-50 text-red-700'
        "
      >
        {{ notification.message }}
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import UserSelect from "@/components/selects/UserSelect";
import * as XLSX from "xlsx";

export default {
  name: "RegisterTrackingCode",
  components: { UserSelect },
  data() {
    return {
      orders: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 20,
      loading: false,
      submittingOrderId: null,
      responsibleId: "",
      skipSms: false,
      trackingCodes: {},
      importFile: null,
      importUploadedFileId: null,
      importSourceData: null,
      importLoading: false,
      importSubmitting: false,
      importPreview: [],
      selectedImportOrderIds: [],
      importSummary: { total: 0, valid: 0, invalid: 0 },
      importProgress: {
        show: false,
        done: false,
        total: 0,
        completed: 0,
        succeeded: 0,
        failed: 0,
        activeBatches: 0,
        smsFailedOrderIds: [],
        failedBatches: [],
      },
      filters: {
        orderId: "",
        phone: "",
        customerName: "",
      },
      notification: {
        show: false,
        message: "",
        type: "success",
      },
    };
  },
  computed: {
    selectedImportCostTotal() {
      const selected = new Set(this.selectedImportOrderIds);
      return this.importPreview.reduce((total, row) =>
        total + (row.status === "matched" && selected.has(row.order_id)
          ? Number(row.actual_shipping_cost || 0) * 10 : 0), 0);
    },
    pageOffset() {
      return (this.currentPage - 1) * this.pageSize;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
    importProgressPercent() {
      if (!this.importProgress.total) return 0;
      return Math.min(
        100,
        Math.round(
          (this.importProgress.completed / this.importProgress.total) * 100
        )
      );
    },
    importProgressResultMessage() {
      if (!this.importProgress.done) return "";
      if (this.importProgress.failed) {
        return `${this.importProgress.succeeded} سفارش ثبت شد و ${this.importProgress.failed} سفارش ثبت نشد. سفارش‌های ناموفق برای تلاش مجدد انتخاب باقی مانده‌اند.`;
      }
      return `کد رهگیری هر ${this.importProgress.succeeded} سفارش با موفقیت ثبت شد و وضعیت آن‌ها به ارسال‌شده تغییر کرد.`;
    },
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    buildListUrl() {
      const params = [`limit=${this.pageSize}`, `offset=${this.pageOffset}`];
      if (this.filters.orderId)
        params.push(`id=${encodeURIComponent(this.filters.orderId)}`);
      if (this.filters.phone)
        params.push(`customer_phone=${encodeURIComponent(this.filters.phone)}`);
      if (this.filters.customerName)
        params.push(
          `customer_name=${encodeURIComponent(this.filters.customerName)}`
        );
      return `shop/trackingCodeOrderList/all?${params.join("&")}`;
    },
    fetchOrders(silent = false) {
      this.loading = true;
      const requestOptions = {
        url: this.endpoint(this.buildListUrl()),
        method: "get",
        success: (data) => {
          this.orders = data.results || [];
          this.totalCount = data.count || 0;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.notify(this.errorMessage(error), "error");
        },
      };

      if (silent) {
        this.requestWithoutGlobalLoading(requestOptions)
          .then(requestOptions.success)
          .catch(requestOptions.error);
        return;
      }
      this.request(requestOptions);
    },
    requestWithoutGlobalLoading(options) {
      const headers = {};
      if (options.token !== false) this.appendToken(headers);

      return axios
        .request({
          headers,
          url: options.url,
          method: options.method || "get",
          data: options.data,
          params: options.params,
          timeout: 300000,
        })
        .then((response) => {
          const data = response.data;
          if (data && data.token) this.setToken();
          return data;
        });
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchOrders();
    },
    resetFilters() {
      this.filters = { orderId: "", phone: "", customerName: "" };
      this.currentPage = 1;
      this.fetchOrders();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchOrders();
    },
    normalizedTrackingCode(orderId) {
      return this.toEnglishDigits(this.trackingCodes[orderId] || "").replace(
        /\s/g,
        ""
      );
    },
    toEnglishDigits(value) {
      return String(value)
        .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
        .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));
    },
    setTrackingCode(orderId, value) {
      const normalized = this.toEnglishDigits(value)
        .replace(/\D/g, "")
        .slice(0, 24);
      this.$set(this.trackingCodes, orderId, normalized);
    },
    handleImportFile(event) {
      const file = event.target.files && event.target.files[0];
      this.importFile = file || null;
      this.importUploadedFileId = null;
      this.importSourceData = null;
      this.importPreview = [];
      this.selectedImportOrderIds = [];
      this.importSummary = { total: 0, valid: 0, invalid: 0 };
    },
    normalizeImportHeader(value) {
      return this.toEnglishDigits(value || "")
        .replace(/[يى]/g, "ی")
        .replace(/ك/g, "ک")
        .replace(/[\s\u200c_-]/g, "")
        .trim();
    },
    matrixToImportRows(matrix) {
      if (!Array.isArray(matrix) || matrix.length < 2) {
        throw new Error("فایل ردیف اطلاعات ندارد.");
      }
      const headers = matrix[0].map((value) =>
        this.normalizeImportHeader(value)
      );
      const sourceIndex = headers.indexOf("نامگ");
      const trackingIndex = headers.indexOf("بارکد");
      const costIndex = headers.indexOf("هزینهکل");
      if (costIndex === -1) {
        throw new Error("ستون «هزینه کل» در فایل پیدا نشد.");
      }
      if (sourceIndex === -1 || trackingIndex === -1) {
        throw new Error("ستون‌های «نام گ» و «بارکد» در فایل پیدا نشدند.");
      }
      return matrix
        .slice(1)
        .map((row, index) => ({
          row_number: index + 2,
          source_value: String(row[sourceIndex] || "").trim(),
          tracking_code: this.normalizedTrackingValue(row[trackingIndex]),
          shipping_cost_rials: String(row[costIndex] == null ? "" : row[costIndex]).trim(),
        }))
        .filter(
          (row) =>
            (row.source_value || row.tracking_code) &&
            this.normalizeImportHeader(row.tracking_code) !== "جمعکل"
        );
    },
    normalizedTrackingValue(value) {
      return this.toEnglishDigits(value || "").replace(/\s/g, "");
    },
    parseHtmlSpreadsheet(text) {
      const documentNode = new DOMParser().parseFromString(text, "text/html");
      const table = documentNode.querySelector("table");
      if (!table) throw new Error("جدولی در فایل پیدا نشد.");
      const matrix = Array.from(table.querySelectorAll("tr")).map((row) =>
        Array.from(row.querySelectorAll("th, td")).map((cell) =>
          (cell.textContent || "").trim()
        )
      );
      return {
        type: "html",
        text,
        rows: this.matrixToImportRows(matrix),
      };
    },
    parseRealSpreadsheet(buffer) {
      const workbook = XLSX.read(buffer, { type: "array", raw: false });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) throw new Error("فایل هیچ برگه‌ای ندارد.");
      const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: "",
        raw: false,
      });
      return {
        type: "xlsx",
        buffer,
        rows: this.matrixToImportRows(matrix),
      };
    },
    async extractImportRows(file) {
      if (file.size > 10 * 1024 * 1024)
        throw new Error("حجم فایل نباید بیشتر از ۱۰ مگابایت باشد.");
      const buffer = await file.arrayBuffer();
      const prefix = new TextDecoder("utf-8")
        .decode(buffer.slice(0, 256))
        .trimStart()
        .toLowerCase();
      if (
        prefix.startsWith("<table") ||
        prefix.startsWith("<html") ||
        prefix.startsWith("<!doctype")
      ) {
        return this.parseHtmlSpreadsheet(
          new TextDecoder("utf-8").decode(buffer)
        );
      }
      return this.parseRealSpreadsheet(buffer);
    },
    async previewImportFile() {
      if (!this.importFile || this.importLoading) return;
      this.importLoading = true;
      try {
        const sourceData = await this.extractImportRows(this.importFile);
        const rows = sourceData.rows;
        if (!rows.length) throw new Error("فایل ردیف قابل بررسی ندارد.");
        this.importSourceData = sourceData;
        this.requestImportPreview(rows);
      } catch (error) {
        this.importLoading = false;
        this.notify(error.message || "خواندن فایل انجام نشد.", "error");
      }
    },
    requestImportPreview(rows) {
      this.importLoading = true;
      this.request({
        url: this.endpoint("shop/previewTrackingCodeImport"),
        method: "post",
        data: { rows },
        success: (data) => {
          this.importPreview = data.rows || [];
          this.importSummary = data.summary || {
            total: 0,
            valid: 0,
            invalid: 0,
          };
          this.selectedImportOrderIds = this.importPreview
            .filter((row) => row.status === "matched")
            .map((row) => row.order_id);
          this.importLoading = false;
        },
        error: (error) => {
          this.importLoading = false;
          this.notify(this.errorMessage(error), "error");
        },
      });
    },
    selectImportCandidate(row, value) {
      if (!this.importSourceData || this.importLoading) return;
      const sourceRow = this.importSourceData.rows.find(
        (item) => Number(item.row_number) === Number(row.row_number)
      );
      if (!sourceRow) return;
      if (value) {
        this.$set(sourceRow, "selected_order_id", Number(value));
      } else {
        this.$delete(sourceRow, "selected_order_id");
      }
      this.requestImportPreview(this.importSourceData.rows);
    },
    clearImport() {
      if (this.importSubmitting) return;
      this.importFile = null;
      this.importUploadedFileId = null;
      this.importSourceData = null;
      this.importPreview = [];
      this.selectedImportOrderIds = [];
      this.importSummary = { total: 0, valid: 0, invalid: 0 };
      if (this.$refs.importFile) this.$refs.importFile.value = "";
    },
    candidateOrders(row) {
      return row.candidate_order_ids && row.candidate_order_ids.length
        ? row.candidate_order_ids.join("، ")
        : "—";
    },
    importCandidateLabel(candidate) {
      const name = candidate.customer_name || "بدون نام";
      const phone = candidate.customer_phone || "بدون موبایل";
      return `${candidate.id} - ${name} - ${phone} - ${candidate.status_label}`;
    },
    importRowClass(status) {
      if (status === "matched" || status === "registered")
        return "bg-green-50/40";
      if (status === "ambiguous") return "bg-yellow-50/60";
      return "bg-red-50/40";
    },
    importStatusClass(status) {
      return status === "matched" || status === "registered"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700";
    },
    importResultText(row) {
      if (row.status === "matched") {
        return `پیدا شد - سفارش ${row.order_id}`;
      }
      return row.message || "این ردیف قابل ثبت نیست.";
    },
    importResultFileName(extension) {
      const baseName = (
        this.importFile && this.importFile.name
          ? this.importFile.name
          : "نتیجه-تطبیق"
      ).replace(/\.[^.]+$/, "");
      return `${baseName}-نتیجه.${extension}`;
    },
    downloadBlob(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    downloadHtmlImportResult() {
      const documentNode = new DOMParser().parseFromString(
        this.importSourceData.text,
        "text/html"
      );
      const table = documentNode.querySelector("table");
      if (!table) throw new Error("جدول فایل برای ساخت نتیجه پیدا نشد.");

      const resultByRow = new Map(
        this.importPreview.map((row) => [
          Number(row.row_number),
          this.importResultText(row),
        ])
      );
      Array.from(table.querySelectorAll("tr")).forEach((tableRow, index) => {
        const resultCell = documentNode.createElement(
          index === 0 ? "th" : "td"
        );
        resultCell.textContent =
          index === 0 ? "نتیجه تطبیق" : resultByRow.get(index + 1) || "";
        resultCell.style.border = "1px solid #d1d5db";
        resultCell.style.padding = "6px";
        if (index === 0) {
          resultCell.style.backgroundColor = "#dbeafe";
          resultCell.style.fontWeight = "bold";
        } else if (resultByRow.has(index + 1)) {
          const previewRow = this.importPreview.find(
            (row) => Number(row.row_number) === index + 1
          );
          resultCell.style.backgroundColor =
            previewRow && previewRow.status === "matched"
              ? "#dcfce7"
              : "#fee2e2";
        }
        tableRow.appendChild(resultCell);
      });

      const output = `<!doctype html><html dir="rtl"><head><meta charset="utf-8"></head><body>${table.outerHTML}</body></html>`;
      this.downloadBlob(
        new Blob(["\ufeff", output], {
          type: "application/vnd.ms-excel;charset=utf-8",
        }),
        this.importResultFileName("xls")
      );
    },
    downloadXlsxImportResult() {
      const workbook = XLSX.read(this.importSourceData.buffer.slice(0), {
        type: "array",
        raw: false,
      });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) throw new Error("برگه فایل برای ساخت نتیجه پیدا نشد.");
      const range = XLSX.utils.decode_range(sheet["!ref"] || "A1:A1");
      const resultColumn = range.e.c + 1;

      XLSX.utils.sheet_add_aoa(sheet, [["نتیجه تطبیق"]], {
        origin: { r: 0, c: resultColumn },
      });
      this.importPreview.forEach((row) => {
        XLSX.utils.sheet_add_aoa(sheet, [[this.importResultText(row)]], {
          origin: { r: Number(row.row_number) - 1, c: resultColumn },
        });
      });
      XLSX.writeFile(workbook, this.importResultFileName("xlsx"), {
        bookType: "xlsx",
      });
    },
    downloadImportResult() {
      if (!this.importSourceData || !this.importPreview.length) {
        this.notify("ابتدا پیش‌نمایش فایل را نمایش دهید.", "error");
        return;
      }
      try {
        if (this.importSourceData.type === "html") {
          this.downloadHtmlImportResult();
        } else {
          this.downloadXlsxImportResult();
        }
      } catch (error) {
        this.notify(error.message || "ساخت فایل نتیجه انجام نشد.", "error");
      }
    },
    resetImportProgress(total) {
      this.importProgress = {
        show: true,
        done: false,
        total,
        completed: 0,
        succeeded: 0,
        failed: 0,
        activeBatches: 0,
        smsFailedOrderIds: [],
        failedBatches: [],
      };
    },
    closeImportProgress() {
      if (!this.importProgress.done) return;
      this.importProgress.show = false;
    },
    splitImportEntries(entries) {
      const batchSize = Math.max(3, Math.ceil(entries.length / 40));
      const batches = [];
      for (let index = 0; index < entries.length; index += batchSize) {
        batches.push(entries.slice(index, index + batchSize));
      }
      return batches;
    },
    submitImportBatch(entries, responsibleId, sendSms) {
      return this.requestWithoutGlobalLoading({
        url: this.endpoint("shop/confirmTrackingCodeImport"),
        method: "post",
        data: {
          entries,
          file_id: this.importUploadedFileId,
          changed_by_id: responsibleId,
          send_sms: sendSms,
        },
      });
    },
    async processImportBatches(entries) {
      const batches = this.splitImportEntries(entries);
      const responsibleId = this.responsibleId;
      const sendSms = !this.skipSms;
      const succeededOrderIds = [];
      const failedOrderIds = [];
      let nextBatchIndex = 0;

      const worker = async () => {
        while (nextBatchIndex < batches.length) {
          const batchIndex = nextBatchIndex;
          nextBatchIndex += 1;
          const batch = batches[batchIndex];
          this.importProgress.activeBatches += 1;
          try {
            const data = await this.submitImportBatch(
              batch,
              responsibleId,
              sendSms
            );
            const updatedOrderIds =
              data.updated_order_ids || batch.map((entry) => entry.order_id);
            succeededOrderIds.push(...updatedOrderIds);
            this.importProgress.succeeded += updatedOrderIds.length;
            this.importProgress.smsFailedOrderIds = Array.from(
              new Set([
                ...this.importProgress.smsFailedOrderIds,
                ...(data.sms_failed_order_ids || []),
              ])
            );
          } catch (error) {
            const orderIds = batch.map((entry) => entry.order_id);
            failedOrderIds.push(...orderIds);
            this.importProgress.failed += batch.length;
            this.importProgress.failedBatches.push({
              orderIds,
              message: this.errorMessage(error),
            });
          } finally {
            this.importProgress.completed += batch.length;
            this.importProgress.activeBatches -= 1;
          }
        }
      };

      const workerCount = Math.min(4, batches.length);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      return { succeededOrderIds, failedOrderIds };
    },
    async confirmImport() {
      if (!this.responsibleId) {
        this.notify("ابتدا مسئول ثبت کد پیگیری را انتخاب کنید.", "error");
        return;
      }
      const selectedIds = new Set(this.selectedImportOrderIds);
      const entries = this.importPreview
        .filter(
          (row) => row.status === "matched" && selectedIds.has(row.order_id)
        )
        .map((row) => ({
          order_id: row.order_id,
          tracking_code: row.tracking_code,
          shipping_cost_rials: row.shipping_cost_rials,
        }));
      if (!entries.length || this.importSubmitting) return;
      if (
        !window.confirm(
          `کد رهگیری ${entries.length} سفارش ثبت و وضعیت آن‌ها ارسال‌شده شود؟ ${
            this.skipSms ? "پیامک ارسال نمی‌شود." : "پیامک ارسال می‌شود."
          }`
        )
      )
        return;

      this.importSubmitting = true;
      try {
        if (!this.importUploadedFileId) {
          const formData = new FormData();
          formData.append("file", this.importFile);
          const uploaded = await this.requestWithoutGlobalLoading({
            url: this.endpoint("shop/uploadTrackingCodeImport"),
            method: "post",
            data: formData,
          });
          if (!uploaded.id) throw new Error("ذخیره فایل انجام نشد.");
          this.importUploadedFileId = uploaded.id;
        }
      } catch (error) {
        this.importSubmitting = false;
        this.notify(this.errorMessage(error), "error");
        return;
      }
      this.resetImportProgress(entries.length);
      const { succeededOrderIds, failedOrderIds } =
        await this.processImportBatches(entries);
      const succeededIds = new Set(succeededOrderIds.map(Number));
      const uniqueFailedOrderIds = Array.from(
        new Set(failedOrderIds.map(Number))
      );

      this.importPreview = this.importPreview.map((row) =>
        succeededIds.has(Number(row.order_id))
          ? {
              ...row,
              status: "registered",
              message: "کد رهگیری ثبت شد.",
            }
          : row
      );
      this.selectedImportOrderIds = uniqueFailedOrderIds;
      this.importSummary = {
        ...this.importSummary,
        valid: uniqueFailedOrderIds.length,
      };
      this.importProgress.done = true;
      this.importProgress.activeBatches = 0;
      this.importSubmitting = false;
      this.fetchOrders(true);

      if (!uniqueFailedOrderIds.length) this.clearImport();
    },
    submitTrackingCode(order) {
      const trackingCode = this.normalizedTrackingCode(order.id);
      if (!this.responsibleId) {
        this.notify("ابتدا مسئول ثبت کد پیگیری را انتخاب کنید.", "error");
        return;
      }
      if (!trackingCode) {
        this.notify("کد رهگیری را وارد کنید.", "error");
        return;
      }
      if (!/^\d{24}$/.test(trackingCode)) {
        this.notify(
          "کد رهگیری باید دقیقاً ۲۴ رقم و فقط شامل عدد باشد.",
          "error"
        );
        return;
      }
      if (this.submittingOrderId !== null) return;
      if (
        !window.confirm(
          `کد رهگیری «${trackingCode}» برای سفارش ${
            order.id
          } ثبت و سفارش ارسال‌شده شود؟ ${
            this.skipSms ? "پیامک ارسال نمی‌شود." : "پیامک ارسال می‌شود."
          }`
        )
      )
        return;

      this.submittingOrderId = order.id;
      this.request({
        url: this.endpoint("shop/registerTrackingCodeAndShip"),
        method: "post",
        data: {
          order_id: order.id,
          tracking_code: trackingCode,
          changed_by_id: this.responsibleId,
          send_sms: !this.skipSms,
        },
        success: (data) => {
          this.$delete(this.trackingCodes, order.id);
          this.submittingOrderId = null;
          const smsFailed = (data.sms_failed_order_ids || []).length > 0;
          this.notify(
            smsFailed
              ? `کد رهگیری سفارش ${order.id} ثبت شد و سفارش ارسال‌شده شد؛ اما پیامک ارسال نشد.`
              : `کد رهگیری سفارش ${
                  order.id
                } ثبت شد و سفارش به ارسال‌شده تغییر کرد${
                  this.skipSms ? "؛ پیامکی ارسال نشد" : ""
                }.`,
            smsFailed ? "error" : "success"
          );
          this.fetchOrders();
        },
        error: (error) => {
          this.submittingOrderId = null;
          this.notify(this.errorMessage(error), "error");
        },
      });
    },
    customerName(order) {
      const customer = order.customer || {};
      const address = order.shipment_address || {};
      const customerFullName = `${customer.first_name || ""} ${
        customer.last_name || ""
      }`.trim();
      const receiverFullName = `${address.first_name || ""} ${
        address.last_name || ""
      }`.trim();
      return customerFullName || receiverFullName || "-";
    },
    customerPhone(order) {
      if (!order.customer) return "-";
      return order.customer.mobile_number || order.customer.username || "-";
    },
    formatDate(value) {
      return value ? value.split("T")[0] : "-";
    },
    errorMessage(error) {
      const data = error && error.response ? error.response.data : null;
      if (typeof data === "string") {
        if (/<(?:!doctype|html|head|body)\b/i.test(data)) {
          return "خطای داخلی سرور رخ داد. گزارش خطا را در لاگ سرور بررسی کنید.";
        }
        return data;
      }
      if (data && data.error) return data.error;
      if (data && data.detail) return data.detail;
      return "عملیات انجام نشد. دوباره تلاش کنید.";
    },
    notify(message, type = "success") {
      this.notification = { show: true, message, type };
      setTimeout(() => {
        this.notification.show = false;
      }, 5000);
    },
  },
};
</script>

<style scoped>
table th,
table td {
  vertical-align: middle;
}
</style>
